// components
import GridPageAbstract from '../../GridPageAbstract';
import DialogAbstract from './../../DialogAbstract';

import {
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Typography,
    DialogActions,
    Button,
    TextField,
    CircularProgress
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import i18 from '../../../i18';
import { ShopBulkIcon } from '../../../icons-lib/bulkIcons';
import config from '../../../config';
import {
    CloseCircleCustomLinearIcon,
    HistoryLogCustomLinearIcon
} from '../../../icons-lib';
import React from 'react';
import { purple } from '@mui/material/colors';

export default class MerchantsPage extends GridPageAbstract {
    constructor(props) {
        super(props);

        // Main settings
        this.Name = 'merchants';
        this.Server = this.props.context.getState().server.merchants;
        this.hideSearch = false;
        this.searchFields = [
            'name',
            'email',
            'country',
            'city',
            'street',
            'zip',
            'link'
        ];
        this.permissionName = 'approve-merchant';
        this.headerStartIcon = ShopBulkIcon;
        this.bannerImage = config?.bannerImages?.transactions || '';
        this.hiddenColumns = config?.hiddenColumns?.merchants || [];

        // Table columns
        this.Head = [
            { id: 'name', label: 'name', alignRight: false, cell: 'Text' },
            { id: 'email', label: 'email', alignRight: false, cell: 'Text' },
            {
                id: 'categories',
                label: 'categories',
                alignRight: false,
                cell: 'Array',
                display: 'name',
                primary: 'name',
                from: 'merchantCategory',
                filterCell: 'Context',
                filterBy: 'merchantCategory',
                filterField: 'categories.id'
            },
            { id: 'street', label: 'street', alignRight: false, cell: 'Text' },
            { id: 'zip', label: 'zip', alignRight: false, cell: 'Text' },
            { id: 'city', label: 'city', alignRight: false, cell: 'Text' },
            {
                id: 'province',
                label: 'province',
                alignRight: false,
                cell: 'Entity',
                filterCell: 'Items',
                filterBy: 'province',
                filterField: 'province',
                items: config.provinces
            },
            {
                id: 'country',
                label: 'country',
                alignRight: false,
                cell: 'Entity',
                filterCell: 'Items',
                filterBy: 'countries',
                filterField: 'country',
                items: config.countries
            },
            { id: 'link', label: 'link', alignRight: false, cell: 'Text' },
            {
                id: 'subtitle',
                label: 'subtitle',
                alignRight: false,
                cell: 'Text'
            },
            {
                id: 'shortDescription',
                label: 'shortDescription',
                alignRight: false,
                cell: 'Text'
            },
            {
                id: 'approvalState',
                label: 'approvalState',
                alignRight: false,
                cell: 'Status',
                controller: 'merchants',
                fieldIdName: 'merchantId',
                disableEditor: true,
                filterCell: 'Items',
                filterBy: 'approvalState',
                items: ['active', 'rejected', 'pending'],
                needTranslate: true,
                permissionName: 'approve-merchant'
            },
            {
                id: 'approvedUser',
                label: 'approvedUser',
                alignRight: false,
                cell: 'Text',
                display: 'firstName+lastName',
                fallbackDisplay: 'email',
                object: 'approvedUser',
                disableEditor: true,
                filterCell: 'Context',
                filterBy: 'users'
            },
            {
                id: 'files',
                label: 'files',
                alignRight: false,
                cell: 'Upload',
                fileType: 'merchant',
                type: 'merchantUpload',
                uploadType: 'multiple',
                fileParent: 'id',
                disableEditor: true
            },
            {
                id: 'dateCreated',
                label: 'dateCreated',
                alignRight: false,
                cell: 'Date',
                filterCell: 'Date',
                disableEditor: true
            },
            {
                id: 'dateModified',
                label: 'dateModified',
                alignRight: false,
                cell: 'Date',
                filterCell: 'Date',
                disableEditor: true
            }
        ].filter((column) => !this.hiddenColumns.includes(column.id));

        // Initial state
        this.state = {
            ...this.state,
            isHistoryOpen: false,
            historyData: [],
            selectedMerchant: null,
            postSaveDialogOpen: false,
            postSaveData: null,
            modificationReason: '',
            isSubmitting: false
        };

        this.handleHistoryLogOpen = this.handleHistoryLogOpen.bind(this);
        this.handleHistoryLogClose = this.handleHistoryLogClose.bind(this);

        this.handleMerchantSave = this.handleMerchantSave.bind(this);
        this.handleReasonChange = this.handleReasonChange.bind(this);
        this.handlePostSaveDialogClose =
            this.handlePostSaveDialogClose.bind(this);
        this.submitWithReason = this.submitWithReason.bind(this);
    }

    async handleHistoryLogOpen(merchant) {
        this.setState({
            selectedMerchant: this.state.menuClickItemId,
            isHistoryOpen: true
        });
        try {
            const res = await fetch(
                `/api/merchants/activity-log/${this.state.menuClickItemId}`,
                { headers: { 'X-Auth-Token': localStorage.token } }
            );
            const json = await res.json();
            this.setState({ historyData: json.data });
        } catch (e) {
            console.error('Failed to load history:', e);
        }
    }

    // New onSave for DialogAbstract
    handleMerchantSave(data) {
        // Open our dialog immediately, without autosave
        this.setState({
            postSaveDialogOpen: true,
            postSaveData: data,
            modificationReason: ''
        });
    }

    handleReasonChange(e) {
        this.setState({ modificationReason: e.target.value });
    }

    handlePostSaveDialogClose() {
        this.setState({ postSaveDialogOpen: false, postSaveData: null, isSubmitting: false });
    }

    // When user clicks OK in reason dialog
    async submitWithReason() {
        this.setState({ isSubmitting: true });
        const { postSaveData, modificationReason } = this.state;
        const user = this.props.context.getState().userInfo;

        // Now call standard save with data + reason + modifiedBy
        const payload = {
            ...postSaveData,
            modificationReason,
            modifiedBy: user
        };

        // Now call base save
        await super.handleDialogOnSave(payload);

        // Form request body for logging
        const logPayload = {
            email: user.email, // for example, "admin2@gmail.com"
            reason: modificationReason // for example, "test2"
        };

        try {
            const logRes = await fetch('/api/merchants/activity-log/latest', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Auth-Token': localStorage.token
                },
                body: JSON.stringify(logPayload)
            });
            if (!logRes.ok) {
                throw new Error(`Error logging: ${logRes.status}`);
            }
            const logResult = await logRes.json();
        } catch (err) {
            console.error('Failed to write activity-log:', err);
            // here you can show an error to the user if you wish
        }

        // Close our dialog
        this.handlePostSaveDialogClose();
    }

    // Override renderDialog to pass our onSave
    renderDialog() {
        return (
            <DialogAbstract
                dialogOpen={this.state.dialogOpen}
                dialogData={this.state.dialogData}
                permissions={this.permissions}
                Name={this.Name}
                Head={this.Head}
                Server={this.Server}
                // Instead of super.handleDialogOnSave — our interceptor
                onSave={this.handleMerchantSave}
                onClose={() => this.handleDialogOnClose()}
            />
        );
    }

    // Render our dialog with reason
    renderPostSaveDialog() {
        const { postSaveDialogOpen, postSaveData, modificationReason, isSubmitting } =
            this.state;
        return (
            <Dialog
                open={postSaveDialogOpen}
                onClose={this.handlePostSaveDialogClose}
                fullWidth
                maxWidth="sm"
            >
                <DialogContent dividers>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Modification reason"
                        type="text"
                        fullWidth
                        value={modificationReason}
                        onChange={this.handleReasonChange}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={this.handlePostSaveDialogClose}>
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        onClick={this.submitWithReason}
                        disabled={!modificationReason.trim() || isSubmitting}
                        startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                    >
                        {isSubmitting ? "Saving..." : i18.t('OK')}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    handleHistoryLogClose() {
        this.setState({ isHistoryOpen: false, historyData: [] });
    }

    renderCustomMenuItems() {
        if (!this.allowedAction(this.permissionName)) return null;
        const merchant = this.state.rowData;

        return [
            this.allowedAction('approve-merchant') && (
                <MenuItem
                    key="approve"
                    onClick={() => this.handleApproveDialogOpen(merchant)}
                    sx={{ color: 'success.main' }}
                >
                    <TaskAltIcon sx={{ mr: 2 }} />
                    {i18.t('buttons.approve')}
                </MenuItem>
            ),
            this.allowedAction('approve-merchant') && (
                <MenuItem
                    key="review"
                    onClick={() => this.handleRejectDialogOpen(merchant)}
                >
                    <CloseCircleCustomLinearIcon sx={{ mr: 2 }} />
                    {i18.t('buttons.decline')}
                </MenuItem>
            ),
            this.allowedAction('approve-merchant') && (
                <MenuItem
                    key="historyLog"
                    onClick={() => this.handleHistoryLogOpen(merchant)}
                >
                    <HistoryLogCustomLinearIcon sx={{ mr: 2 }} />
                    {i18.t('buttons.historyLog')}
                </MenuItem>
            )
        ];
    }

    renderHistoryDialog() {
        const { isHistoryOpen, historyData, selectedMerchant } = this.state;
        // This is a hook, but we'll get the theme directly from the class
        const theme = this.props.context.theme;
        // or set it in state/props if you use ThemeProvider.

        const formatDate = (iso) => {
            const d = new Date(iso);
            const time = d.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
            });
            const date = d.toLocaleDateString('en-US', {
                month: 'short',
                day: '2-digit',
                year: 'numeric'
            });
            return `${time} · ${date}`;
        };

        return (
            <Dialog
                open={isHistoryOpen}
                onClose={this.handleHistoryLogClose}
                fullWidth
                maxWidth="lg"
            >
                <DialogTitle sx={{ m: 0, p: 2, fontWeight: 600, fontSize: 20 }}>
                    {i18.t('History Log', { name: selectedMerchant?.name })}
                    <IconButton
                        aria-label="close"
                        onClick={this.handleHistoryLogClose}
                        sx={{ position: 'absolute', right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent dividers>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <Typography
                                        variant="subtitle2"
                                        color="textSecondary"
                                    >
                                        {i18.t('Changed Field (Old)')}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="subtitle2"
                                        color="textSecondary"
                                    >
                                        {i18.t('Changed Field (New)')}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="subtitle2"
                                        color="textSecondary"
                                    >
                                        {i18.t('Date & Time')}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="subtitle2"
                                        color="textSecondary"
                                    >
                                        {i18.t('Changed by')}
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="subtitle2"
                                        color="textSecondary"
                                    >
                                        {i18.t('Reason')}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {historyData.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={5} align="center">
                                        <Typography color="textSecondary">
                                            {i18.t('historyLog.noEntries')}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}

                            {historyData.map((row) => {
                                const isChanged =
                                    row.changedFieldOld !== row.changedFieldNew;
                                return (
                                    <TableRow key={row.id}>
                                        {/* OLD */}
                                        <TableCell
                                            sx={{
                                                verticalAlign: 'top',
                                                py: 1.5
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                sx={{
                                                    textTransform: 'capitalize',
                                                    mb: 0.5,
                                                    display: 'block',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {row.fieldName}
                                            </Typography>

                                            {row.changedFieldOld ? (
                                                <Typography
                                                    variant="body2"
                                                    color="textPrimary"
                                                >
                                                    {row.changedFieldOld}
                                                </Typography>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    sx={{ fontStyle: 'italic' }}
                                                >
                                                    (Blank)
                                                </Typography>
                                            )}
                                        </TableCell>

                                        {/* NEW */}
                                        <TableCell
                                            sx={{
                                                verticalAlign: 'top',
                                                py: 1.5
                                            }}
                                        >
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                                sx={{
                                                    textTransform: 'capitalize',
                                                    mb: 0.5,
                                                    display: 'block',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                {row.fieldName}
                                            </Typography>

                                            {row.changedFieldNew ? (
                                                <Typography
                                                    variant="body2"
                                                    // highlight orange if value changed
                                                    color={
                                                        isChanged
                                                            ? 'warning.main'
                                                            : 'textPrimary'
                                                    }
                                                >
                                                    {row.changedFieldNew}
                                                </Typography>
                                            ) : (
                                                <Typography
                                                    variant="body2"
                                                    color="textSecondary"
                                                    sx={{ fontStyle: 'italic' }}
                                                >
                                                    (Blank)
                                                </Typography>
                                            )}
                                        </TableCell>

                                        {/* DATE */}
                                        <TableCell sx={{ py: 1.5 }}>
                                            <Typography
                                                variant="body2"
                                                color="textSecondary"
                                            >
                                                {formatDate(row.dateCreated)}
                                            </Typography>
                                        </TableCell>

                                        {/* CHANGED BY */}
                                        <TableCell sx={{ py: 1.5 }}>
                                            <Typography
                                                variant="body2"
                                                sx={{ color: purple[600], fontWeight: 500 }}
                                            >
                                                {row.changedBy}
                                            </Typography>
                                        </TableCell>

                                        {/* REASON */}
                                        <TableCell sx={{ py: 1.5 }}>
                                            <Typography
                                                variant="body2"
                                                color="textPrimary"
                                            >
                                                {row.reason}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </DialogContent>

                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={this.handleHistoryLogClose}>
                        {i18.t('Close')}
                    </Button>
                </DialogActions>
            </Dialog>
        );
    }

    render() {
        return (
            <>
                {super.render()}
                {this.renderPostSaveDialog()}
                {this.renderHistoryDialog()}
            </>
        );
    }

    customRetrieveData = async (list) => {
        const files = list.flatMap((item) => item.files);
        await this.preloadContextImages(files);
    };
}
